declare global {
    type Color = number;
}

const hex = (num: number) => num.toString(16).padStart(2, "0");

export namespace Color {
    export function fromRGBA(r: number, g: number, b: number, a: number = 255) {
        return (a << 24) | (b << 16) | (g << 8) | r;
    }

    export function fromHex(str: string) {
        if (str.startsWith("#")) {
            str = str.slice(1);
        }
        if (str.length === 3 || str.length === 4) {
            str = str.replace(/./g, "$&$&");
        }
        if (str.length === 6) {
            str = str + "FF";
        }
        if (str.length !== 8) {
            throw new Error("Invalid color string");
        }

        // split every 2 characters, reverse the array, and join it back together
        str = str.match(/.{2}/g)!.reverse().join("");
        return parseInt(str, 16);
    }

    export function fromHSLA(h: number, s: number, l: number, alpha: number = 255) {
        l /= 100;
        const a = (s * Math.min(l, 1 - l)) / 100;
        const f = (n: number) => {
            const k = (n + h / 30) % 12;
            const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
            return 255 * color;
        };

        return fromRGBA(f(0), f(8), f(4), alpha);
    }

    export function asRGBA(color: Color) {
        return {
            r: color & 0xff,
            g: (color >>> 8) & 0xff,
            b: (color >>> 16) & 0xff,
            a: (color >>> 24) & 0xff,
        };
    }

    export function asHex(color: Color) {
        return (
            "#" +
            hex(color & 0xff) +
            hex((color >>> 8) & 0xff) +
            hex((color >>> 16) & 0xff) +
            hex((color >>> 24) & 0xff)
        );
    }

    export function asHSLA(color: Color) {
        let { r, g, b, a } = asRGBA(color);
        r /= 255;
        g /= 255;
        b /= 255;

        const l = Math.max(r, g, b);
        const s = l - Math.min(r, g, b);
        const h = s ? (l === r ? (g - b) / s : l === g ? 2 + (b - r) / s : 4 + (r - g) / s) : 0;
        return {
            h: 60 * h < 0 ? 60 * h + 360 : 60 * h,
            s: 100 * (s ? (l <= 0.5 ? s / (2 * l - s) : s / (2 - (2 * l - s))) : 0),
            l: (100 * (2 * l - s)) / 2,
            a,
        };
    }

    // Predefined colors
    export const black = fromHex("#000000");
    export const white = fromHex("#FFFFFF");
    export const red = fromHex("#FF0000");
    export const green = fromHex("#00FF00");
    export const blue = fromHex("#0000FF");

    export const transparent = fromHex("#00000000");
}
